import React, { createContext, useContext, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark";
export type ThemeSetting = "auto" | "light" | "dark";

export interface ThemeContextType {
  theme: ThemeMode;               // tema aplicado
  themeSetting: ThemeSetting;     // preferencia do usuário
  setThemeSetting: (value: ThemeSetting) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [themeSetting, setThemeSetting] = useState<ThemeSetting>("auto");
  const [theme, setTheme] = useState<ThemeMode>("light");

  // Detecta tema do SO
  const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)");

  const applyTheme = (mode: ThemeMode) => {
    document.documentElement.setAttribute("data-theme", mode);
    setTheme(mode);
  };

  // Reage a mudanças do usuário
  useEffect(() => {
    if (themeSetting === "auto") {
      applyTheme(systemPrefersDark.matches ? "dark" : "light");
    } else {
      applyTheme(themeSetting);
    }
  }, [themeSetting]);

  // Reage a mudanças no sistema
  useEffect(() => {
    const handler = () => {
      if (themeSetting === "auto") {
        applyTheme(systemPrefersDark.matches ? "dark" : "light");
      }
    };

    systemPrefersDark.addEventListener("change", handler);
    return () => systemPrefersDark.removeEventListener("change", handler);
  }, [themeSetting]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeSetting,
        setThemeSetting,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
